import { Button, Col, Input, Row, Select, message } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import React, { FunctionComponent, useState } from 'react'
import { syncWithHubspot } from 'services'
import styled from 'styled-components'
import { mutate } from 'swr'

import DatePicker from 'modules/EditableFields/DatePicker'

import * as T from 'types'

import { SubsectionTitle } from './Styles'

const Container = styled.div`
  margin-bottom: 20px;
`

const FieldContainer = styled.div`
  margin-bottom: 15px;
`

const ErrorContainer = styled.div`
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 10px;
  color: #cf1322;
`

interface IProps {
  listing: T.IListing
  update: T.IUpdateListing
}

const HomeRiseData: FunctionComponent<IProps> = ({ listing, update }) => {
  const [hubSpotLink, setHubSpotLink] = useState<string>(
    listing.hubSpotLink ? String(listing.hubSpotLink) : '',
  )

  const [hubspotDealId, setHubSpotDealId] = useState<string>(
    listing.hubspotDealId ? String(listing.hubspotDealId) : '',
  )

  const [hrLaunchCallStatus, setHrLaunchCallStatus] = useState<string>(
    listing.hrLaunchCallStatus ? String(listing.hrLaunchCallStatus) : 'Not scheduled',
  )

  const [hrLaunchCallAssignee, setHrLaunchCallAssignee] = useState<string>(
    listing.hrLaunchCallAssignee ? String(listing.hrLaunchCallAssignee) : '',
  )

  const [hrLaunchCallScheduledAt, setHrLaunchCallScheduledAt] = useState<Dayjs | null>(
    listing.hrLaunchCallScheduledAt ? dayjs(String(listing.hrLaunchCallScheduledAt)) : null,
  )

  const [isEditing, setIsEditing] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)

  const hrLaunchCallStatusOptions = [
    { label: 'Not scheduled', value: 'Not scheduled' },
    { label: 'Scheduled', value: 'Scheduled' },
    { label: 'Missed', value: 'Missed' },
    { label: 'Completed', value: 'Completed' },
  ]

  if (!listing.isHomeRise) {
    return null
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleSave = async () => {
    await update({
      hubSpotLink,
      hubspotDealId,
      hrLaunchCallStatus,
      hrLaunchCallAssignee,
      hrLaunchCallScheduledAt: hrLaunchCallScheduledAt
        ? hrLaunchCallScheduledAt.toISOString()
        : null,
    })
    setIsEditing(false)
  }

  const handleCancel = () => {
    setHubSpotLink(listing.hubSpotLink ? String(listing.hubSpotLink) : '')
    setHubSpotDealId(listing.hubspotDealId ? String(listing.hubspotDealId) : '')
    setHrLaunchCallStatus(
      listing.hrLaunchCallStatus ? String(listing.hrLaunchCallStatus) : 'Not scheduled',
    )
    setHrLaunchCallAssignee(
      listing.hrLaunchCallAssignee ? String(listing.hrLaunchCallAssignee) : '',
    )
    setHrLaunchCallScheduledAt(
      listing.hrLaunchCallScheduledAt ? dayjs(String(listing.hrLaunchCallScheduledAt)) : null,
    )
    setIsEditing(false)
  }

  const handleSynchronize = async () => {
    setIsSyncing(true)
    try {
      const [success, error] = await syncWithHubspot(listing._id)

      if (success) {
        message.success('Successfully synchronized with HubSpot')
        // Refetch the listing data to get updated sync status
      } else {
        message.error(`Synchronization failed: ${error?.message || 'Unknown error'}`)
      }
    } catch (error) {
      message.error('Failed to synchronize with HubSpot')
      console.error('Sync error:', error)
    } finally {
      await mutate(`/listings/${listing._id}/ownerDetails`)
      setIsSyncing(false)
    }
  }

  return (
    <Container>
      <SubsectionTitle>Hubspot Data</SubsectionTitle>
      <Row gutter={[15, 15]}>
        <Col span={12}>
          <FieldContainer>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>HubSpot Link</div>
            {isEditing
              ? (
                <div>
                  <Input
                    value={hubSpotLink}
                    onChange={e => setHubSpotLink(e.target.value)}
                    style={{ marginBottom: '10px' }}
                  />
                </div>
              )
              : (
                <div style={{ marginBottom: '10px' }}>
                  {listing.hubSpotLink
                    ? (
                      <a
                        href={String(listing.hubSpotLink)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {String(listing.hubSpotLink)}
                      </a>
                    )
                    : (
                      'No HubSpot link specified'
                    )}
                </div>
              )}
          </FieldContainer>
          <FieldContainer>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>HubSpot Deal ID</div>
            {isEditing
              ? (
                <div>
                  <Input
                    value={hubspotDealId}
                    onChange={e => setHubSpotDealId(e.target.value)}
                    style={{ marginBottom: '10px' }}
                  />
                </div>
              )
              : (
                <div style={{ marginBottom: '10px' }}>
                  {listing.hubspotDealId
                    ? String(listing.hubspotDealId)
                    : 'No HubSpot Deal ID specified'}
                </div>
              )}
          </FieldContainer>
          <FieldContainer>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>HR Launch Call Status</div>
            {isEditing
              ? (
                <div>
                  <Select
                    value={hrLaunchCallStatus}
                    onChange={setHrLaunchCallStatus}
                    options={hrLaunchCallStatusOptions}
                    style={{ width: '100%', marginBottom: '10px' }}
                  />
                </div>
              )
              : (
                <div style={{ marginBottom: '10px' }}>{hrLaunchCallStatus}</div>
              )}
          </FieldContainer>
          <FieldContainer>
            <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>Launch Call Details</div>
            <FieldContainer>
              <div style={{ marginBottom: '5px' }}>Team Member Scheduled</div>
              {isEditing
                ? (
                  <Input
                    value={hrLaunchCallAssignee}
                    onChange={e => setHrLaunchCallAssignee(e.target.value)}
                    placeholder="Enter team member name"
                    style={{ marginBottom: '10px' }}
                  />
                )
                : (
                  <div style={{ marginBottom: '10px' }}>{hrLaunchCallAssignee || '—'}</div>
                )}
            </FieldContainer>
            <FieldContainer>
              <div style={{ marginBottom: '5px' }}>Date & Time of Call</div>
              {isEditing
                ? (
                  <DatePicker
                    showTime
                    value={hrLaunchCallScheduledAt}
                    onChange={setHrLaunchCallScheduledAt}
                    format="MM/DD/YYYY h:mm A"
                    style={{ width: '100%', marginBottom: '10px' }}
                  />
                )
                : (
                  <div style={{ marginBottom: '10px' }}>
                    {hrLaunchCallScheduledAt
                      ? hrLaunchCallScheduledAt.format('MM/DD/YYYY h:mm A')
                      : '—'}
                  </div>
                )}
            </FieldContainer>
          </FieldContainer>
          {isEditing
            ? (
              <div>
                <Button
                  type="primary"
                  onClick={handleSave}
                  style={{ marginRight: '10px' }}
                >
                Save
                </Button>
                <Button onClick={handleCancel}>Cancel</Button>
              </div>
            )
            : (
              <Button
                type="primary"
                onClick={handleEdit}
              >
              Edit
              </Button>
            )}
        </Col>
        <Col span={12}>
          <FieldContainer>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Synchronize with HubSpot</div>
            {listing.hubspotSyncError && (
              <ErrorContainer>
                <strong>Sync Error:</strong> {listing.hubspotSyncError}
              </ErrorContainer>
            )}
            <Button
              type="primary"
              onClick={handleSynchronize}
              loading={isSyncing}
              disabled={isEditing}
            >
              {isSyncing ? 'Synchronizing...' : 'Synchronize'}
            </Button>
          </FieldContainer>
        </Col>
      </Row>
    </Container>
  )
}

export default HomeRiseData
